/**
 * Classify a thrown fetch() failure so logs are self-explanatory and the error
 * stream stays signal-rich. fetch() rejects (rather than resolving) only for
 * transport-level problems, so the buckets are:
 *
 *  - "aborted":  the request was cancelled (AbortController) — user navigated
 *                away, started a new request, or the component unmounted. Expected.
 *  - "timeout":  an AbortSignal.timeout() fired. Usually slow client network.
 *  - "network":  the browser never got an HTTP response — offline, connection
 *                reset, DNS, or a blocked request ("Failed to fetch" / "Load
 *                failed" / "NetworkError"). Almost always transient & client-side.
 *  - "error":    anything else — genuinely unexpected, keep it at error level.
 *
 * Only "error" is logged at error severity; the transient kinds are warnings,
 * and "aborted" is always info once resolveFetchFailureLevel has had its say —
 * an AbortError is by definition our own code cancelling (queryClient
 * .cancelQueries() on sign-out, an unmount, a superseded request), never a
 * product or network fault. Query future occurrences with
 * `@failure_kind:network` (etc.) and alert on a *rate* of them, not
 * individual events.
 */

export type FetchFailureKind = "aborted" | "timeout" | "network" | "error"

export interface FetchFailureClass {
  kind: FetchFailureKind
  level: "warn" | "error"
}

// Browser network-failure messages across engines (Chrome/Electron, Safari, Firefox).
const NETWORK_MESSAGE = /failed to fetch|load failed|networkerror|network request failed/i

export function classifyFetchFailure(err: unknown): FetchFailureClass {
  const name = err instanceof Error ? err.name : ""
  const message = err instanceof Error ? err.message : String(err)

  if (name === "AbortError") return { kind: "aborted", level: "warn" }
  if (name === "TimeoutError") return { kind: "timeout", level: "warn" }
  if (name === "TypeError" && NETWORK_MESSAGE.test(message)) {
    return { kind: "network", level: "warn" }
  }
  return { kind: "error", level: "error" }
}

// ── Environment-explained transport failures ─────────────────────────────
// A thrown fetch on a page that is offline or already hidden is the browser
// doing exactly what it must: the network stack is down, or the tab is being
// closed/backgrounded and its in-flight requests cancelled. Logging those at
// warn made every laptop-lid close and tab close count against the warn
// budget — the 2026-08-26 log triage found the http_request_failed +
// auth_session_probe_unreachable clusters were entirely this class (plus
// datacenter bots). A transient failure only deserves warn when the
// environment does NOT explain it: an online, visible page. Genuinely
// unexpected failures ("error" kind) keep error severity regardless — an
// unclassified throw is worth seeing even from a hidden tab.
//
// "aborted" is info regardless of environment. Since /api/user/me started
// honouring React Query's signal, queryClient.cancelQueries() on sign-out
// (Navbar/HelpNavbar) aborts the real request — and that is our own code
// doing exactly what it was asked to, on an online, visible page. The same
// holds for the auth probe in lib/auth/client.ts: an aborted probe is a
// cancellation we issued, not a fault worth a warn.
export function resolveFetchFailureLevel(
  classified: FetchFailureClass,
  env: { online?: boolean; pageVisibility?: string }
): "info" | "warn" | "error" {
  if (classified.level === "error") return "error"
  if (classified.kind === "aborted") return "info"
  if (env.online === false || env.pageVisibility === "hidden") return "info"
  return classified.level
}

// ── Deliberate non-2xx protocol responses ────────────────────────────────
// Some endpoints use an error status as a signal, not a failure; logging them
// at warn buries real warnings (this class was part of ~30% of warn volume in
// the 2026-08-14 log audit). Only responses that are a designed client
// contract belong here:
//
//  - 409 /api/user/me — refresh-rotation grace. A parallel request just
//    rotated the refresh cookie; the route deliberately answers
//    409 + Retry-After (app/api/user/me/route.ts) and the client retries once
//    (lib/hooks/use-user.ts). Expected and self-healing → info, the same rule
//    the desktop http logger applies to this response.
//  - Named checkout/desktop conflicts require both the exact path and server
//    response code. The UI offers Billing or an explicit device takeover; an
//    unrelated conflict, malformed response or auth failure remains a warning.
export function isExpectedHttpStatus(status: number, requestPath: string, body?: unknown): boolean {
  // requestPath is a parsed pathname for absolute URLs but may be the raw
  // relative string fetch() was given — strip query/hash before comparing.
  const path = requestPath.split(/[?#]/, 1)[0]
  if (status !== 409) return false
  if (path === "/api/user/me") return true
  if (!body || typeof body !== "object") return false
  const code = (body as { code?: unknown }).code
  return (path === "/api/payments/create-checkout" && code === "subscription_exists") ||
    (path === "/api/auth/desktop-handoff" && code === "DESKTOP_ALREADY_ACTIVE")
}
